import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { getCurrentOrgId, getGuidsToSkip, getPrincipalObjectExportLimit, isPrincipalImportExportEnabled, isScriptabilityEnabled, isTmlObjectIdEnabled, orgStatisticsCountV2Enabled, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { useBlinkContextForAdmin } from "../../../contexts/admin-blink-context";
import { ValidEdocMetadataType } from "../../../services/generated/graphql-types";
import { useDeleteGroupMutation, useGetEdocLazyQuery, useGetGroupsPrincipalListQuery, useGetOrgStatisticsMutation, } from "../../../services/user-group/user-group-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { getTranslatedInfoForMessageCode } from "../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../utils/error-handling/error-utils";
import { BaseComponentContainer } from '../base-component/base-component.container';
import { ListContainer } from '../list-container/list.container';
import { getErrorCode, getSelectedOnCurrentPage, ImportTslPath, isExportObjectLimitReached, MODE, onRowsSelectAction, refreshCells, updateRowData, } from '../util';
import { CreateEditModal } from './create-edit-modal/create-edit-modal.container';
import { DeleteConfirmationModal } from './delete-confirmation-modal/delete-confirmation-modal';
import { ExportTMLOptions } from './ExportGroupTMLoptions';
import styles from './groups.module.scss';
import { ExportTML } from './groupsExportModel';
import { useDataHooks } from './useDataHook';
import { downloadExportedGroups, getColumnDefinitions, getToastMessage, groupParams, ListActionType, sortKeyMap, tableColumnHeaders, TranslationsKeys, } from './util';
import { ViewPrivilegeModal } from './view-privileges-modal/view-privilege-modal.container';
export var ImportEPackAction;
(function (ImportEPackAction) {
    ImportEPackAction["Create"] = "CREATE";
    ImportEPackAction["Update"] = "UPDATE";
})(ImportEPackAction || (ImportEPackAction = {}));
export const GroupsContainer = () => {
    const { navService } = useBlinkContextForAdmin();
    const { t } = useTranslation();
    const { metadataService, metadataList } = BasePageContext();
    const isTmlObjectIdFlagEnabled = isTmlObjectIdEnabled();
    const currentData = useRef();
    const gridRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [errorBanner, setErrorBanner] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState(null);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [mode, setMode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefresh, setIsRefresh] = useState(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpened] = useState(false);
    const [lastOperatedPage, setLastOperatedPage] = useState(0);
    const [isViewPrivilegesModalOpen, setIsViewPrivilegesModalOpen] = useState(false);
    const [groupListInput, setGroupListInput] = useState(() => ({
        ...groupParams,
        skipIds: getGuidsToSkip(),
    }));
    const [selectedRowsIds, setSelectedRowsIds] = useState([]);
    const currentOrgId = getCurrentOrgId();
    const [shouldExportDependencies, setShouldExportDependencies] = useState(true);
    const [hasInitialOrgCount, setHasInitialOrgCount] = useState(false);
    const sortData = useRef({
        sort: groupParams.sort,
        sortAscending: groupParams.sortAscending,
    });
    const { data, error, loading, isLastBatch } = useDataHooks(useGetGroupsPrincipalListQuery, isRefresh, groupListInput, setIsLoading, setIsRefresh);
    const { getOrgStatistics, count: orgCount, loading: orgCountLoading, error: orgCountError, } = useGetOrgStatisticsMutation();
    const { getEdoc: getEdocLazyQuery, data: edocData, loading: isGetEdocLoading, error: isEdocError, } = useGetEdocLazyQuery();
    const exportGroups = (groupIds) => {
        setIsLoading(true);
        const payload = groupIds.map(id => ({
            id,
            type: ValidEdocMetadataType.UserGroup,
        }));
        getEdocLazyQuery(payload, shouldExportDependencies, false, false, isTmlObjectIdFlagEnabled, isTmlObjectIdFlagEnabled);
    };
    const showErrorBanner = (message) => {
        setErrorBanner(true);
        setErrorBannerMessage(message);
    };
    useEffect(() => {
        if (isEdocError && !isGetEdocLoading && !edocData) {
            showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                limit: getPrincipalObjectExportLimit(),
            }));
            setIsLoading(false);
            setIsExportModalOpened(false);
        }
        else if (edocData && !isGetEdocLoading) {
            downloadExportedGroups(edocData, false);
            setIsLoading(false);
            setIsExportModalOpened(false);
            setSelectedRowsIds([]);
        }
    }, [edocData, isGetEdocLoading, isEdocError]);
    useEffect(() => {
        if (!hasInitialOrgCount && orgStatisticsCountV2Enabled()) {
            getOrgStatistics('GROUP', {
                allOrgs: currentOrgId === -1,
            });
            setHasInitialOrgCount(true);
        }
    }, [hasInitialOrgCount, currentOrgId]);
    const { data: deleteGroupData, error: deleteGroupError, loading: deleteGroupLoading, deleteGroup, } = useDeleteGroupMutation();
    const hideErrorBanner = () => {
        setErrorBanner(false);
        setErrorBannerMessage(null);
    };
    const refreshOrgCount = useCallback(() => {
        if (!orgStatisticsCountV2Enabled()) {
            return;
        }
        getOrgStatistics('GROUP', {
            allOrgs: currentOrgId === -1,
        });
    }, [getOrgStatistics, currentOrgId]);
    const successBannerMode = (successMode) => {
        setMode(successMode);
        setIsToastAlertVisible(true);
        setIsRefresh(true);
        hideErrorBanner();
        if (successMode === MODE.DELETE || successMode === MODE.ADD) {
            refreshOrgCount();
        }
    };
    const onChangeOffset = (offset) => {
        const { sort, sortAscending } = sortData.current;
        setGroupListInput({
            ...groupListInput,
            offset,
            sort,
            sortAscending,
            skipIds: getGuidsToSkip(),
        });
        setIsLoading(true);
    };
    const noDataProps = {
        title: t('NO_MATCH_MSG'),
        description: '',
        img: ErrorCode.GLOBAL_DATA,
    };
    const onSearchChange = (term) => {
        const { sortAscending } = sortData.current;
        setGroupListInput({
            ...groupListInput,
            offset: 0,
            sortAscending,
            pattern: term,
            skipIds: getGuidsToSkip(),
        });
        setIsLoading(true);
    };
    const onClickAddGroupBtn = () => {
        hideErrorBanner();
        setIsModalOpened(true);
        setIsEditMode(false);
        setIsLoading(true);
    };
    const onClickedImportButton = () => {
        localStorage.setItem('allOrgsOverride', 'false');
        navService.goToPath(ImportTslPath.USER_GROUP);
    };
    const onClickDeleteGroupBtn = useCallback(() => {
        hideErrorBanner();
        setIsDeleteModalOpen(true);
    }, [isDeleteModalOpen]);
    const renderActionsBtn = (text, onClick) => (React.createElement(Button, { text: t(TranslationsKeys[text]), onClick: onClick, type: "secondary" }));
    const isTmlImportExportEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const getActionBar = useCallback(() => (React.createElement(Horizontal, { className: styles.actionHandlerWrapper },
        React.createElement(React.Fragment, null,
            React.createElement(Vertical, { className: styles.gridSearch },
                React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                        onSearchChange(searchTerm);
                    }, className: styles.searchBarContainer, "data-testid": "search-bar-filters" })),
            !selectedRowsIds.length ? (React.createElement(Horizontal, { spacing: "d" },
                renderActionsBtn('ADD_GROUP_BUTTON_TITLE', onClickAddGroupBtn),
                isTmlImportExportEnabled &&
                    renderActionsBtn('IMPORT_GROUP_TITLE', onClickedImportButton))) : (React.createElement(Horizontal, { spacing: "d" },
                isTmlImportExportEnabled &&
                    renderActionsBtn('EXPORT', () => {
                        if (isExportObjectLimitReached(selectedRowsIds)) {
                            showErrorBanner(t(TranslationsKeys.EXPORT_LIMIT_ERROR, {
                                limit: getPrincipalObjectExportLimit(),
                            }));
                        }
                        else {
                            setIsExportModalOpened(true);
                        }
                    }),
                renderActionsBtn('DELETE', onClickDeleteGroupBtn)))))), [selectedRowsIds, rowData]);
    const onRowsSelected = (rowIds) => {
        onRowsSelectAction(rowIds, data, groupListInput, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds);
    };
    useEffect(() => {
        const filterredSelectedRowIds = getSelectedOnCurrentPage(selectedRowsIds, data);
        if (gridRef.current) {
            updateRowData(rowData, filterredSelectedRowIds, setRowData);
            refreshCells(gridRef, filterredSelectedRowIds, tableColumnHeaders.MORE_OPTION);
        }
    }, [selectedRowsIds]);
    useEffect(() => {
        updateRowData(data, getSelectedOnCurrentPage(selectedRowsIds, data), setRowData);
        setIsLoading(false);
    }, [data]);
    const openEditModal = useCallback((rowData) => {
        currentData.current = rowData;
        setIsModalOpened(true);
        setIsEditMode(true);
        setIsLoading(true);
    }, []);
    const onRowActionCB = ({ action, data: actionData }) => {
        currentData.current = actionData;
        if (action === ListActionType.EDIT) {
            openEditModal(actionData);
        }
        else if (action === ListActionType.DELETE) {
            setIsDeleteModalOpen(true);
        }
        else if (action === ListActionType.EXPORT) {
            exportGroups([actionData.id]);
        }
        else if (action === ListActionType.VIEW_ALL_PRIVILEGES) {
            setIsViewPrivilegesModalOpen(true);
        }
    };
    const onDeleteModalClose = useCallback(() => {
        setIsDeleteModalOpen(false);
    }, [isDeleteModalOpen]);
    const onViewPrivilegesModalClose = useCallback(() => {
        setIsViewPrivilegesModalOpen(false);
    }, [isViewPrivilegesModalOpen]);
    const onDeleteConfirm = async () => {
        hideErrorBanner();
        let ids = [currentData.current?.id];
        if (selectedRowsIds.length > 0) {
            ids = [...selectedRowsIds];
        }
        await deleteGroup(ids)
            .then(res => {
            setSelectedRowsIds([]);
            successBannerMode(MODE.DELETE);
            onDeleteModalClose();
            const { offset, batchSize } = groupListInput;
            if (offset && ids.length === rowData.length) {
                onChangeOffset(offset - batchSize);
            }
            return '';
        })
            .catch(err => {
            const errorCode = getErrorCode(getErrorObjectFromGraphQLErrors(err));
            const errorText = t(getTranslatedInfoForMessageCode(errorCode).summary);
            setErrorBannerMessage(errorText);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onDeleteModalClose();
        });
    };
    const errorConfig = {
        errorBanner,
        errorBannerMessage,
        hideErrorBanner,
    };
    const onSortingChanged = (sortState) => {
        const sortingColumn = sortState.find((el) => {
            return el.sort !== 'null';
        }) || sortState[0];
        const sortKey = sortingColumn &&
            sortKeyMap[sortingColumn.colId];
        const ascending = sortingColumn ? sortingColumn.sort === 'asc' : true;
        sortData.current.sort = sortKey;
        sortData.current.sortAscending = ascending;
        setGroupListInput({
            ...groupListInput,
            sort: sortKey,
            offset: 0,
            sortAscending: ascending,
            skipIds: getGuidsToSkip(),
        });
        setIsLoading(true);
    };
    const setAgGridApi = (agGridApi) => {
        gridRef.current = agGridApi;
    };
    const suppressNavigation = ({ colDef, }) => colDef.colId === tableColumnHeaders.MORE_OPTION;
    const listViewProps = {
        data: rowData,
        columns: getColumnDefinitions(),
        options: {
            suppressMovable: true,
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
        },
        onSortingChanged,
        loading,
        setAgGridApi,
        suppressKeyboardEvent: suppressNavigation,
    };
    const onExportModalRadioButtonChange = (event) => {
        setShouldExportDependencies(event.target.value === ExportTMLOptions.OBJECT_WITH_DEPENDENCIES);
    };
    const shouldShowOrgCount = orgStatisticsCountV2Enabled() &&
        !orgCountLoading &&
        currentOrgId !== -1;
    return (React.createElement(BaseComponentContainer, { id: "groups-page", container: "groupsContainer", title: shouldShowOrgCount
            ? `${t(TranslationsKeys.PAGE_TITLE)} (${orgCount ?? 0})`
            : t(TranslationsKeys.PAGE_TITLE), error: errorConfig },
        React.createElement(Vertical, null,
            React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading },
                React.createElement(Vertical, null,
                    getActionBar(),
                    React.createElement(ListContainer, { testId: "groups-list-page", data: data, listViewProps: listViewProps, noDataProps: noDataProps, offSet: groupListInput.offset, paginationSize: groupListInput.batchSize, isLastBatch: isLastBatch, onChangeOffset: onChangeOffset, headerHeight: 32, isLoading: isLoading, onKeyDown: e => {
                            const focusedCell = gridRef.current?.getFocusedCell();
                            if (e.key === 'Enter' &&
                                gridRef.current
                                    ?.getFocusedCell()
                                    ?.column?.getColId() ===
                                    tableColumnHeaders.GROUP_NAME) {
                                const rowNode = gridRef.current.getDisplayedRowAtIndex(focusedCell.rowIndex);
                                if (rowNode) {
                                    openEditModal(rowNode.data);
                                }
                            }
                        } }, metadataList)),
                isModalOpened && (React.createElement(CreateEditModal, { isModalOpened: isModalOpened, closeModal: () => {
                        setIsModalOpened(!isModalOpened);
                    }, isEditMode: isEditMode, setIsLoading: setIsLoading, successBannerMode: successBannerMode, groupId: currentData.current?.id })),
                isDeleteModalOpen && (React.createElement(DeleteConfirmationModal, { name: currentData.current?.groupDisplayName.text || '', isModalOpen: isDeleteModalOpen, onDeleteConfirm: onDeleteConfirm, onDeleteModalClose: onDeleteModalClose, isMultiSelected: selectedRowsIds.length > 0, showLoader: deleteGroupLoading })),
                isExportModalOpen && (React.createElement(ExportTML, { onRadioButtonChange: onExportModalRadioButtonChange, onModalDismiss: () => {
                        setIsExportModalOpened(false);
                    }, getEdoc: () => exportGroups(selectedRowsIds), isGetEdocLoading: isGetEdocLoading, shouldExportDependencies: shouldExportDependencies, isExportDependenciesModalOpen: isExportModalOpen })),
                isViewPrivilegesModalOpen && (React.createElement(ViewPrivilegeModal, { closeModal: onViewPrivilegesModalClose, id: currentData.current?.id }))),
            React.createElement(GlobalAlert.Toast, { message: getToastMessage(mode, 0), isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true }))));
};
export const Groups = () => React.createElement(GroupsContainer, null);
//# sourceMappingURL=groups.container.js.map